/** Editable Sticky Notes in meeting view when the user can upsert and the API has not disabled editing. View stays available without upsert. */
export const shouldShowMeetingViewStickyNoteColumn = (canUpsertStickyNote: boolean, showStickyNoteFromApi?: boolean | null): boolean =>
    canUpsertStickyNote && (showStickyNoteFromApi ?? true);

/**
 * Fixed master-list columns for Meeting View (list + side panel).
 * Order is intentional and not user-configurable in this release.
 */
export const MEETING_VIEW_MASTER_LIST_COLUMN_IDS = ['id', 'fin', 'roomBed', 'patientName', 'totalLos', 'flags', 'stickyNote'] as const;

export const MEETING_VIEW_MASTER_LIST_COLUMN_ID_SET = new Set<string>(MEETING_VIEW_MASTER_LIST_COLUMN_IDS);

/** API-backed columns that must always remain visible in meeting view. */
export const MEETING_VIEW_REQUIRED_COLUMN_IDS = new Set<string>(['fin', 'roomBed', 'patientName', 'totalLos', 'stickyNote']);

/** Frontend-only column ids — never sent to meeting-view preferences API. */
export const MEETING_VIEW_UI_ONLY_COLUMN_IDS = new Set<string>(['llosFlagNote', 'sortSeq', 'dcToday', 'flags']);

/** UI-only columns that can be dragged and kept in the UI column order. */
export const MEETING_VIEW_ORDERABLE_UI_ONLY_COLUMN_IDS = new Set<string>(['llosFlagNote']);

/**
 * UI column id ↔ API preference column id.
 * Table columns must use `keyof CCTPatient`; the preferences API uses dotted paths or aliases.
 * Tier uses `aiDischargeReadiness.tier`; Medical Readiness uses `aiDischargeReadiness.medicalReadiness`.
 * Legacy bare `aiDischargeReadiness` from the API is aliased to the Tier column (`.tier`).
 */
export const MEETING_VIEW_UI_TO_API_COLUMN_ID: Record<string, string> = {
    id: 'aiDischargeReadiness.tier',
    aiDischargeReadiness: 'aiDischargeReadiness.medicalReadiness',
    lastCmProgressAssessmentAdditionalNote: 'cnAssessment.cnAdditionalNotes',
    llosFlagNote: 'llosNote'
};

export const MEETING_VIEW_API_TO_UI_COLUMN_ID: Record<string, string> = Object.fromEntries(
    Object.entries(MEETING_VIEW_UI_TO_API_COLUMN_ID).map(([uiId, apiId]) => [apiId, uiId])
);

/**
 * Legacy prefs / BE alias: bare `aiDischargeReadiness` means Tier.
 * Must not live in API_TO_UI — that key is also the Medical Readiness UI column id.
 */
export const normalizeMeetingViewApiColumnIds = (columnIds: string[]): string[] =>
    columnIds.map((columnId) => (columnId === 'aiDischargeReadiness' ? 'aiDischargeReadiness.tier' : columnId));

const MEETING_VIEW_NON_HIDEABLE_COLUMN_IDS = new Set<string>([
    ...MEETING_VIEW_REQUIRED_COLUMN_IDS,
    ...[...MEETING_VIEW_UI_ONLY_COLUMN_IDS].filter((columnId) => !MEETING_VIEW_ORDERABLE_UI_ONLY_COLUMN_IDS.has(columnId)),
    'llosNote'
]);

export const isMeetingViewColumnHideable = (columnId: string): boolean => !MEETING_VIEW_NON_HIDEABLE_COLUMN_IDS.has(columnId);

const mapColumnIds = (columnIds: string[], mapping: Record<string, string>): string[] => {
    const next: string[] = [];
    const seen = new Set<string>();

    columnIds.forEach((columnId) => {
        const mappedId = mapping[columnId] ?? columnId;
        if (seen.has(mappedId)) {
            return;
        }
        seen.add(mappedId);
        next.push(mappedId);
    });

    return next;
};

const isMeetingViewUiStateColumnId = (columnId: string): boolean =>
    !MEETING_VIEW_UI_ONLY_COLUMN_IDS.has(columnId) || MEETING_VIEW_ORDERABLE_UI_ONLY_COLUMN_IDS.has(columnId);

/** Visible columns for UI state — maps API ids to UI ids and strips non-orderable frontend-only ids. */
export const getMeetingViewUiVisibleColumnIds = (columnIds: string[]): string[] => {
    const withoutCheckboxColumn = columnIds.includes('llosFlagNote') ? columnIds.filter((columnId) => columnId !== 'llosNote') : columnIds;

    return mapColumnIds(withoutCheckboxColumn, MEETING_VIEW_API_TO_UI_COLUMN_ID).filter(isMeetingViewUiStateColumnId);
};

/** Like getMeetingViewUiVisibleColumnIds, but first rewrites legacy bare readiness → Tier. */
export const getMeetingViewUiVisibleColumnIdsFromApi = (columnIds: string[]): string[] =>
    getMeetingViewUiVisibleColumnIds(normalizeMeetingViewApiColumnIds(columnIds));

export const toggleMeetingViewVisibleColumn = (visibleColumnIds: string[], columnId: string, visible: boolean): string[] => {
    const next = new Set(visibleColumnIds);
    if (visible) {
        next.add(columnId);
    } else {
        next.delete(columnId);
    }
    MEETING_VIEW_REQUIRED_COLUMN_IDS.forEach((requiredColumnId) => next.add(requiredColumnId));
    return getMeetingViewUiVisibleColumnIds([...next]);
};

export const areMeetingViewColumnIdListsEqual = (left: string[], right: string[]): boolean => {
    if (left.length !== right.length) {
        return false;
    }
    return left.every((columnId, index) => columnId === right[index]);
};

export const isMeetingViewStickyColumn = (column: { fixed?: boolean; fixedRight?: boolean }): boolean =>
    Boolean(column.fixed || column.fixedRight);

/** Default neighbor for orderable UI-only columns that are missing from the saved order. */
const MEETING_VIEW_COLUMN_INSERT_AFTER: ReadonlyArray<readonly [string, string]> = [
    ['llosFlagNote', 'totalLos'],
    ['flags', 'patientName']
];

const moveColumnAfter = <T extends { id: string }>(columns: T[], columnId: string, afterColumnId: string): T[] => {
    const fromIndex = columns.findIndex((column) => column.id === columnId);
    const afterIndex = columns.findIndex((column) => column.id === afterColumnId);
    if (fromIndex < 0 || afterIndex < 0) {
        return columns;
    }

    const next = [...columns];
    const [moved] = next.splice(fromIndex, 1);
    const insertAt = next.findIndex((column) => column.id === afterColumnId) + 1;
    next.splice(insertAt, 0, moved);
    return next;
};

const applyMeetingViewDefaultColumnPositions = <T extends { id: string }>(columns: T[], preferredOrder: string[]): T[] =>
    MEETING_VIEW_COLUMN_INSERT_AFTER.reduce((next, [columnId, afterColumnId]) => {
        if (preferredOrder.includes(columnId)) {
            return next;
        }
        return moveColumnAfter(next, columnId, afterColumnId);
    }, columns);

/** Restores dragged UI-only columns after an API payload that omitted them. */
export const mergeMeetingViewUiOnlyColumnOrder = (apiColumnIds: string[], localColumnIds: string[] = []): string[] => {
    const next = getMeetingViewUiVisibleColumnIdsFromApi(apiColumnIds);

    MEETING_VIEW_ORDERABLE_UI_ONLY_COLUMN_IDS.forEach((uiOnlyId) => {
        if (next.includes(uiOnlyId) || !localColumnIds.includes(uiOnlyId)) {
            return;
        }

        const localIndex = localColumnIds.indexOf(uiOnlyId);
        let insertAt = -1;
        for (let i = localIndex - 1; i >= 0; i -= 1) {
            const precedingIndex = next.indexOf(localColumnIds[i]);
            if (precedingIndex >= 0) {
                insertAt = precedingIndex + 1;
                break;
            }
        }
        if (insertAt < 0) {
            const losIndex = next.indexOf('totalLos');
            insertAt = losIndex >= 0 ? losIndex + 1 : 0;
        }
        next.splice(insertAt, 0, uiOnlyId);
    });

    return next;
};

/** Pins sticky columns left/right and applies saved order only to scrollable columns. */
export const applyMeetingViewColumnOrder = <T extends { id: string; fixed?: boolean; fixedRight?: boolean }>(
    columns: T[],
    preferredOrder: string[]
): T[] => {
    if (preferredOrder.length === 0) {
        return applyMeetingViewDefaultColumnPositions(columns, preferredOrder);
    }

    const columnById = new Map(columns.map((column) => [column.id, column] as const));
    const leftSticky: T[] = [];
    const rightSticky: T[] = [];
    const reorderable: T[] = [];

    columns.forEach((column) => {
        if (column.fixed) {
            leftSticky.push(column);
            return;
        }
        if (column.fixedRight) {
            rightSticky.push(column);
            return;
        }
        reorderable.push(column);
    });

    const reorderableIds = new Set(reorderable.map((column) => column.id));
    const orderedMiddle: T[] = [];
    const seen = new Set<string>();

    preferredOrder.forEach((columnId) => {
        if (!reorderableIds.has(columnId) || seen.has(columnId)) {
            return;
        }
        const column = columnById.get(columnId);
        if (!column) {
            return;
        }
        orderedMiddle.push(column);
        seen.add(columnId);
    });

    reorderable.forEach((column) => {
        if (!seen.has(column.id)) {
            orderedMiddle.push(column);
        }
    });

    return applyMeetingViewDefaultColumnPositions([...leftSticky, ...orderedMiddle, ...rightSticky], preferredOrder);
};

export const moveMeetingViewColumn = (
    orderedColumnIds: string[],
    activeColumnId: string,
    overColumnId: string,
    lockedColumnIds: ReadonlySet<string>
): string[] => {
    if (activeColumnId === overColumnId || lockedColumnIds.has(activeColumnId) || lockedColumnIds.has(overColumnId)) {
        return orderedColumnIds;
    }

    const fromIndex = orderedColumnIds.indexOf(activeColumnId);
    const toIndex = orderedColumnIds.indexOf(overColumnId);
    if (fromIndex < 0 || toIndex < 0) {
        return orderedColumnIds;
    }

    const next = [...orderedColumnIds];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
};

export const sanitizeMeetingViewVisibleColumnsForApi = (columnIds: string[]): string[] => {
    const next = new Set(
        mapColumnIds(getMeetingViewUiVisibleColumnIds(columnIds), MEETING_VIEW_UI_TO_API_COLUMN_ID).filter(
            (columnId) => !MEETING_VIEW_UI_ONLY_COLUMN_IDS.has(columnId)
        )
    );
    MEETING_VIEW_REQUIRED_COLUMN_IDS.forEach((columnId) => next.add(columnId));
    return [...next];
};
